import { useState, useEffect, useRef } from 'react';
import { useTMDBMovie } from './useTMDBData';
import { tmdbIdOf } from '../services/tmdbService';

/**
 * Lazy loading hook for TMDB data
 * Only fetches TMDB data when the movie becomes visible in the viewport.
 * Pass the movie's stored TMDB id in options.tmdbId whenever it has one, so
 * the lookup is by id and never a title search.
 */
export const useLazyTMDB = (movieTitle, movieYear, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const elementRef = useRef(null);

  const {
    threshold = 0.1, // How much of the element should be visible before loading
    rootMargin = '50px', // Load when element is 50px away from viewport
    immediate = false, // Set to true to load immediately without intersection
    tmdbId = null
  } = options;

  // Use intersection observer to detect when element becomes visible
  useEffect(() => {
    if (immediate) {
      setShouldLoad(true);
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !shouldLoad) {
          console.log('🎬 Movie became visible, loading TMDB data:', movieTitle);
          setIsVisible(true);
          setShouldLoad(true);
          // Once we start loading, we don't need to observe anymore
          observer.unobserve(element);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [movieTitle, threshold, rootMargin, immediate, shouldLoad]);

  // Only fetch TMDB data when shouldLoad is true
  const { tmdbData, loading, error, refetch } = useTMDBMovie(
    shouldLoad ? movieTitle : null,
    shouldLoad ? movieYear : null,
    shouldLoad ? tmdbId : null
  );

  return {
    tmdbData,
    loading,
    error,
    refetch,
    elementRef,
    isVisible,
    shouldLoad
  };
};

/**
 * Simplified version that only returns basic movie info until loaded
 */
export const useLazyMovieData = (movie, options = {}) => {
  const { elementRef, tmdbData, loading, error, shouldLoad } = useLazyTMDB(
    movie?.title,
    movie?.year,
    { ...options, tmdbId: tmdbIdOf(movie) }
  );

  // Return basic movie info immediately, enhanced data when loaded
  const movieData = shouldLoad ? {
    ...movie,
    ...tmdbData,
    // Prefer TMDB data but fallback to original movie data
    plot: tmdbData?.plot || movie?.plot,
    poster: tmdbData?.poster || movie?.poster,
    director: tmdbData?.director || movie?.director,
    cast: tmdbData?.cast || movie?.cast,
    runtime: tmdbData?.runtime || movie?.runtime
  } : {
    // Only basic info until loaded
    id: movie?.id,
    title: movie?.title,
    year: movie?.year,
    poster: movie?.poster, // Keep original poster as fallback
    isPlaceholder: movie?.isPlaceholder,
    // Add loading indicator
    _lazyLoading: !shouldLoad
  };

  return {
    movieData,
    elementRef,
    loading: shouldLoad ? loading : false,
    error: shouldLoad ? error : null,
    isLazyLoading: !shouldLoad
  };
};
